import m from "mithril";
import { getUserData, getMithril } from "../utils.js";
import toast from "../js/toaster.js";

const CoordinatorAktivitas = {
    coordinators: [],
    selectedCoordinator: null,
    coordinatorActivities: [],
    coordinatorBalances: {},
    coordinatorTodayTotals: {},
    loading: false,
    error: null,
    subunitId: null,
    // User data state for async loading
    userData: null,
    userDataLoading: false,

    // Date Filters
    filterStartDate: null,
    filterEndDate: null,
    expandedDates: {}, // For transaction history accordions

    // Load user data with proper async handling (following Officers.js pattern)
    loadUserData: async function () {
        try {
            console.log('🔍 COORDINATOR AKTIVITAS DEBUG: Loading user data...');
            this.userDataLoading = true;
            m.redraw();

            const userData = await getUserData(true); // Force refresh to get current session
            console.log('🔍 COORDINATOR AKTIVITAS DEBUG: Fresh user data:', userData ? 'Promise resolved' : 'null');

            this.userData = userData;
            this.userDataLoading = false;
            console.log('🔍 COORDINATOR AKTIVITAS DEBUG: User data loaded successfully');
            m.redraw();

            return userData;
        } catch (error) {
            console.error('🔍 COORDINATOR AKTIVITAS DEBUG: Error loading user data:', error);
            this.userDataLoading = false;
            this.error = 'Gagal memuat data pengguna: ' + error.message;
            m.redraw();
            return null;
        }
    },

    async oninit() {
        try {
            console.log('🔍 COORDINATOR AKTIVITAS DEBUG: Component initializing...');

            // Load user data first with retry mechanism
            await this.loadUserDataWithRetry();

            // Initialize date filters (Last 7 days)
            const today = new Date();
            const lastWeek = new Date(today);
            lastWeek.setDate(today.getDate() - 6);

            this.filterEndDate = today.toISOString().split('T')[0];
            this.filterStartDate = lastWeek.toISOString().split('T')[0];

            console.log('🔍 COORDINATOR AKTIVITAS DEBUG: Component initialized successfully');
        } catch (error) {
            console.error('🔍 COORDINATOR AKTIVITAS DEBUG: Error during initialization:', error);
            this.error = 'Gagal inisialisasi komponen: ' + error.message;
            toast.error(this.error);
        }
    },

    // Enhanced user data loading with retry mechanism
    loadUserDataWithRetry: async function (attempts = 3) {
        for (let i = 0; i < attempts; i++) {
            try {
                await this.loadUserData();
                if (this.userData) {
                    console.log('🔍 COORDINATOR AKTIVITAS DEBUG: User data loaded successfully after', i + 1, 'attempts');

                    // Check if user is manajer, superuser, or kasir
                    if (this.userData.role !== 'manajer' && this.userData.role !== 'superuser' && this.userData.role !== 'kasir') {
                        console.log('🔍 COORDINATOR AKTIVITAS DEBUG: Access denied for role:', this.userData.role);
                        m.route.set('/unauthorized');
                        return;
                    }

                    // Set subunit ID
                    this.subunitId = this.userData.subunit_id;
                    console.log('🔍 COORDINATOR AKTIVITAS DEBUG: Subunit ID set to:', this.subunitId);

                    // Load coordinators
                    await this.loadCoordinators();
                    return;
                }
                console.log('🔍 COORDINATOR AKTIVITAS DEBUG: User data still null, retrying...', i + 1);
                await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1))); // Exponential backoff
            } catch (error) {
                console.error('🔍 COORDINATOR AKTIVITAS DEBUG: Error loading user data, attempt', i + 1, ':', error);
                if (i === attempts - 1) {
                    throw error;
                }
            }
        }
    },

    loadCoordinators: async function () {
        this.loading = true;
        this.error = null;
        m.redraw();

        try {
            // Use getMithril for session-based authentication
            const result = await getMithril('/api/officers', { component: this });

            if (result.success) {
                this.coordinators = result.data.filter(officer => {
                    const chair = officer.chair?.toLowerCase();
                    return parseInt(officer.subunitId) === parseInt(this.subunitId) && 
                           (chair === 'koordinator' || chair === 'kasir');
                });

                console.log('🔍 PETUGAS AKTIVITAS DEBUG: Found petugas:', this.coordinators.length);

                // Load balances for coordinators
                await this.loadCoordinatorBalances();
            } else {
                this.error = result.error || 'Gagal memuat koordinator';
                toast.error(this.error);
            }
        } catch (error) {
            console.error('Error loading coordinators:', error);
            this.error = 'Gagal memuat koordinator: ' + error.message;
            toast.error(this.error);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    loadCoordinatorActivities: async function (coordinatorId) {
        this.selectedCoordinator = coordinatorId;
        this.coordinatorActivities = [];
        this.loading = true;
        m.redraw();

        try {
            // Use getMithril for session-based authentication
            const result = await getMithril(`/api/coordinator-management/transactions/${coordinatorId}`, { component: this });

            if (result.success) {
                this.coordinatorActivities = result.data;
            } else {
                toast.error(result.error || 'Gagal memuat aktivitas koordinator');
            }
        } catch (error) {
            console.error('Error loading coordinator activities:', error);
            toast.error('Gagal memuat aktivitas koordinator: ' + error.message);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    loadCoordinatorBalances: async function () {
        try {
            // Use getMithril for session-based authentication
            const result = await getMithril('/api/coordinator-management/balances', { component: this });

            if (result.success) {
                // Create a map of officer_id to balance and today_total
                this.coordinatorBalances = {};
                this.coordinatorTodayTotals = {};
                result.data.forEach(item => {
                    this.coordinatorBalances[item.officer_id] = parseFloat(item.balance) || 0;
                    this.coordinatorTodayTotals[item.officer_id] = parseFloat(item.today_total) || 0;
                });

                console.log('🔍 COORDINATOR AKTIVITAS DEBUG: Loaded balances:', Object.keys(this.coordinatorBalances).length, 'coordinators');
            } else {
                console.warn('Failed to load coordinator balances:', result.error);
                // Set all balances to 0 if we can't load them
                this.coordinators.forEach(coordinator => {
                    this.coordinatorBalances[coordinator.id] = 0;
                });
            }
        } catch (error) {
            console.error('Error loading coordinator balances:', error);
            // Set all balances to 0 if we can't load them
            this.coordinators.forEach(coordinator => {
                this.coordinatorBalances[coordinator.id] = 0;
            });
        }
    },

    formatCurrency: function (amount) {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    },

    formatDate: function (dateString) {
        return new Date(dateString).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    getHeldFunds: function (coordinator) {
        // Return the actual balance from officers_accounts table
        return this.coordinatorBalances[coordinator.id] || 0;
    },

    exportToExcel: function (coordinator) {
        const activitiesToExport = this.getFilteredActivities();
        if (!activitiesToExport || activitiesToExport.length === 0) {
            toast.error("Tidak ada data untuk diekspor");
            return;
        }

        const headers = ["Tanggal", "No Rekening", "Nama Nasabah", "Jenis Transaksi", "Jumlah", "Kategori/Produk"];
        // Numbers must be unquoted in CSV so Excel parses them as numbers (not text).
        const csvText = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const csvNum = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : '0';
        };
        const rows = activitiesToExport.map(activity => {
            const isBanking = activity.collection_type === 'banking';
            const isWithdrawal = activity.collection_type === 'withdrawal' || activity.collection_type === 'loan_disbursement';
            const isPositive = !isBanking && !isWithdrawal;

            const type = activity.collection_type === 'loan_disbursement' ? 'Pencairan Pinjaman' :
                activity.collection_type === 'banking' ? 'Setor ke Kantor' :
                    activity.collection_type === 'withdrawal' ? 'Penarikan Simpanan' :
                        activity.collection_type === 'deposit' ? 'Setoran Simpanan' :
                            activity.collection_type === 'installment' ? 'Angsuran' :
                                activity.collection_type === 'entrusted_funds' ? 'Penitipan Dana Kantor' :
                                    activity.collection_type;

            const amount = (isPositive ? 1 : -1) * Math.abs(Number(activity.collection_amount) || 0);
            const product = activity.product_name || '-';
            const date = this.formatDate(activity.created_at);
            const accountNumber = activity.account_number || '-';
            const customerName = activity.customer_name || '-';

            return [csvText(date), csvText(accountNumber), csvText(customerName), csvText(type), csvNum(amount), csvText(product)].join(',');
        });

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", `Aktivitas_Koordinator_${coordinator.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },

    getFilteredActivities: function () {
        if (!this.coordinatorActivities) return [];

        const start = new Date(this.filterStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(this.filterEndDate);
        end.setHours(23, 59, 59, 999);

        return this.coordinatorActivities.filter(activity => {
            const date = new Date(activity.created_at);
            return date >= start && date <= end;
        });
    },

    getGroupedActivities: function (activities) {
        const groups = {};
        activities.forEach(activity => {
            const date = activity.created_at.split('T')[0];
            if (!groups[date]) groups[date] = [];
            groups[date].push(activity);
        });
        return groups; // Object with date keys
    },

    toggleDateAccordion: function (date) {
        this.expandedDates[date] = !this.expandedDates[date];
    },

    view: function () {
        return m("div", { class: "max-w-7xl mx-auto p-6" }, [
            m("div", { class: "mb-8" }, [
                m("div", { class: "flex items-center space-x-4 mb-2" }, [
                    m("div", { class: "w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg" }, [
                        m("i", { class: "ri-activity-line text-white text-xl" })
                    ]),
                    m("div", [
                        m("h1", { class: "text-3xl font-bold text-gray-900 dark:text-white" }, "Aktivitas Petugas"),
                        m("p", { class: "text-lg text-gray-600 dark:text-gray-400" }, "Pantau aktivitas dan dana yang dikelola oleh petugas (Koordinator/Kasir)")
                    ])
                ]),
                m("div", { class: "h-1 w-20 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full" })
            ]),

            // Error message
            this.error && m("div", { class: "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" }, this.error),

            // Coordinators table
            m("div", { class: "bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-8 border border-gray-200 dark:border-gray-700" }, [
                m("div", { class: "overflow-x-auto" }, [
                    m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                        m("thead", { class: "bg-gradient-to-r from-orange-50 to-orange-100 dark:from-gray-700 dark:to-gray-600" }, [
                            m("tr", [
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Nama Petugas"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Jabatan"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Kode"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Dana Dikelola"),
                                m("th", { class: "px-6 py-4 text-left text-xs font-semibold text-orange-800 dark:text-orange-300 uppercase tracking-wider" }, "Aksi")
                            ])
                        ]),
                        m("tbody", { class: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" }, [
                            this.loading && !this.coordinators.length && m("tr", [
                                m("td", { class: "px-6 py-8 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300", colspan: "5" }, [
                                    m("div", { class: "flex flex-col items-center justify-center space-y-3" }, [
                                        m("i", { class: "ri-loader-4-line animate-spin text-3xl text-orange-600" }),
                                        m("p", { class: "text-gray-600 dark:text-gray-400" }, "Memuat data petugas...")
                                    ])
                                ])
                            ]),
                            !this.loading && this.coordinators.length === 0 && m("tr", [
                                m("td", { class: "px-6 py-8 whitespace-nowrap text-center text-sm text-gray-500 dark:text-gray-300", colspan: "5" }, [
                                    m("div", { class: "flex flex-col items-center justify-center space-y-2" }, [
                                        m("i", { class: "ri-user-star-line text-4xl text-gray-400" }),
                                        m("p", { class: "text-gray-600 dark:text-gray-400" }, "Tidak ada petugas ditemukan")
                                    ])
                                ])
                            ]),
                            this.coordinators.reduce((acc, coordinator, index) => {
                                const heldFunds = this.getHeldFunds(coordinator);
                                const isExpanded = this.selectedCoordinator === coordinator.id;

                                // Calculate stats if expanded and data available
                                let savingsStats = {};
                                let loanStats = {};

                                if (isExpanded && this.coordinatorActivities.length > 0) {
                                    this.coordinatorActivities.forEach(activity => {
                                        const productName = activity.product_name || 'Lainnya';

                                        // Savings
                                        if (activity.collection_type === 'deposit' || activity.collection_type === 'withdrawal') {
                                            if (!savingsStats[productName]) savingsStats[productName] = { collected: 0, withdrawn: 0 };
                                            if (activity.collection_type === 'deposit') {
                                                savingsStats[productName].collected += parseFloat(activity.collection_amount);
                                            } else {
                                                savingsStats[productName].withdrawn += Math.abs(parseFloat(activity.collection_amount));
                                            }
                                        }

                                        // Loans
                                        if (activity.collection_type === 'installment' || activity.collection_type === 'loan_disbursement') {
                                            if (!loanStats[productName]) loanStats[productName] = { disbursed: 0, collected: 0 };
                                            if (activity.collection_type === 'loan_disbursement') {
                                                loanStats[productName].disbursed += Math.abs(parseFloat(activity.collection_amount));
                                            } else if (activity.collection_type === 'installment') {
                                                loanStats[productName].collected += parseFloat(activity.collection_amount);
                                            }
                                        }
                                    });
                                }

                                acc.push(m("tr", {
                                    key: coordinator.id,
                                    class: index % 2 === 0 ? "bg-white dark:bg-gray-800" : "bg-gray-50 dark:bg-gray-750"
                                }, [
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white" }, [
                                        m("div", { class: "flex items-center space-x-3" }, [
                                            m("div", { class: "w-8 h-8 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center" }, [
                                                m("i", { class: "ri-user-star-line text-orange-600 dark:text-orange-400 text-sm" })
                                            ]),
                                            m("span", coordinator.name)
                                        ])
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap" }, [
                                        m("span", { class: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-300" },
                                            coordinator.chair
                                        )
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap" }, [
                                        m("span", { class: "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300" },
                                            coordinator.code
                                        )
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium" }, [
                                        m("div", { class: "flex flex-col" }, [
                                            m("span", {
                                                class: `font-bold ${heldFunds > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`
                                            }, this.formatCurrency(heldFunds)),
                                            this.coordinatorTodayTotals[coordinator.id] > 0 && m("span", {
                                                class: "text-xs font-semibold text-green-500 dark:text-green-400 mt-1 animate-pulse"
                                            }, `+${this.formatCurrency(this.coordinatorTodayTotals[coordinator.id])}`)
                                        ])
                                    ]),
                                    m("td", { class: "px-6 py-4 whitespace-nowrap text-sm font-medium" }, [
                                        m("button", {
                                            onclick: () => {
                                                if (isExpanded) {
                                                    this.selectedCoordinator = null;
                                                    this.coordinatorActivities = [];
                                                } else {
                                                    this.loadCoordinatorActivities(coordinator.id);
                                                }
                                            },
                                            class: "inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-orange-700 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-300 dark:hover:bg-orange-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                                        }, [
                                            m("i", { class: `ri-${isExpanded ? 'arrow-up-s' : 'eye'}-line mr-1.5` }),
                                            isExpanded ? "Tutup" : "Lihat Aktivitas"
                                        ])
                                    ])
                                ]));

                                if (isExpanded) {
                                    const filteredActivities = this.getFilteredActivities();

                                    // Recalculate stats based on filtered data
                                    savingsStats = {};
                                    loanStats = {};

                                    if (filteredActivities.length > 0) {
                                        filteredActivities.forEach(activity => {
                                            const productName = activity.product_name || 'Lainnya';
                                            const date = activity.created_at.split('T')[0];

                                            // Savings
                                            if (activity.collection_type === 'deposit' || activity.collection_type === 'withdrawal') {
                                                if (!savingsStats[productName]) savingsStats[productName] = { collected: 0, withdrawn: 0, daily: {} };

                                                if (activity.collection_type === 'deposit') {
                                                    const amount = parseFloat(activity.collection_amount);
                                                    savingsStats[productName].collected += amount;

                                                    if (!savingsStats[productName].daily[date]) savingsStats[productName].daily[date] = { in: 0, out: 0, in_customers: new Set(), out_customers: new Set() };
                                                    savingsStats[productName].daily[date].in += amount;
                                                    savingsStats[productName].daily[date].in_customers.add(activity.account_number || activity.customer_name || 'unknown');
                                                } else {
                                                    const amount = Math.abs(parseFloat(activity.collection_amount));
                                                    savingsStats[productName].withdrawn += amount;

                                                    if (!savingsStats[productName].daily[date]) savingsStats[productName].daily[date] = { in: 0, out: 0, in_customers: new Set(), out_customers: new Set() };
                                                    savingsStats[productName].daily[date].out += amount;
                                                    savingsStats[productName].daily[date].out_customers.add(activity.account_number || activity.customer_name || 'unknown');
                                                }
                                            }

                                            // Loans
                                            if (activity.collection_type === 'installment' || activity.collection_type === 'loan_disbursement') {
                                                if (!loanStats[productName]) loanStats[productName] = { disbursed: 0, collected: 0, daily: {} };

                                                if (activity.collection_type === 'loan_disbursement') {
                                                    const amount = Math.abs(parseFloat(activity.collection_amount));
                                                    loanStats[productName].disbursed += amount;

                                                    if (!loanStats[productName].daily[date]) loanStats[productName].daily[date] = { in: 0, out: 0, in_customers: new Set(), out_customers: new Set() };
                                                    loanStats[productName].daily[date].out += amount;
                                                    loanStats[productName].daily[date].out_customers.add(activity.account_number || activity.customer_name || 'unknown');
                                                } else if (activity.collection_type === 'installment') {
                                                    const amount = parseFloat(activity.collection_amount);
                                                    loanStats[productName].collected += amount;

                                                    if (!loanStats[productName].daily[date]) loanStats[productName].daily[date] = { in: 0, out: 0, in_customers: new Set(), out_customers: new Set() };
                                                    loanStats[productName].daily[date].in += amount;
                                                    loanStats[productName].daily[date].in_customers.add(activity.account_number || activity.customer_name || 'unknown');
                                                }
                                            }
                                        });
                                    }

                                    const groupedActivities = this.getGroupedActivities(filteredActivities);
                                    const sortedDates = Object.keys(groupedActivities).sort((a, b) => new Date(b) - new Date(a));

                                    acc.push(m("tr", { key: `expanded-${coordinator.id}` }, [
                                        m("td", { colspan: "5", class: "px-0 py-0" }, [
                                            m("div", { class: "bg-gray-50 dark:bg-gray-900 p-6 border-y border-gray-200 dark:border-gray-700 shadow-inner" }, [
                                                this.loading && m("div", { class: "flex items-center justify-center py-8" }, [
                                                    m("i", { class: "ri-loader-4-line animate-spin text-2xl text-orange-600 mr-2" }),
                                                    m("span", { class: "text-gray-600 dark:text-gray-400" }, "Memuat aktivitas...")
                                                ]),

                                                !this.loading && m("div", { class: "space-y-6" }, [
                                                    // Date Filters
                                                    m("div", { class: "bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 flex flex-wrap items-end gap-4" }, [
                                                        m("div", [
                                                            m("label", { class: "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Dari Tanggal"),
                                                            m("input", {
                                                                type: "date",
                                                                class: "block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm bg-white dark:bg-gray-700 dark:text-white",
                                                                value: this.filterStartDate,
                                                                onchange: (e) => this.filterStartDate = e.target.value
                                                            })
                                                        ]),
                                                        m("div", [
                                                            m("label", { class: "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" }, "Sampai Tanggal"),
                                                            m("input", {
                                                                type: "date",
                                                                class: "block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm bg-white dark:bg-gray-700 dark:text-white",
                                                                value: this.filterEndDate,
                                                                onchange: (e) => this.filterEndDate = e.target.value
                                                            })
                                                        ]),
                                                        m("div", { class: "flex space-x-2" }, [
                                                            m("button", {
                                                                onclick: () => {
                                                                    // Reset to last 7 days
                                                                    const today = new Date();
                                                                    const lastWeek = new Date(today);
                                                                    lastWeek.setDate(today.getDate() - 6);
                                                                    this.filterEndDate = today.toISOString().split('T')[0];
                                                                    this.filterStartDate = lastWeek.toISOString().split('T')[0];
                                                                },
                                                                class: "inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                                            }, "Reset 7 Hari"),
                                                        ])
                                                    ]),

                                                    // Savings Stats
                                                    Object.keys(savingsStats).length > 0 && m("div", [
                                                        m("h3", { class: "text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 flex items-center" }, [
                                                            m("i", { class: "ri-safe-2-line mr-2 text-green-600" }),
                                                            "Simpanan"
                                                        ]),
                                                        m("div", { class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" },
                                                            Object.entries(savingsStats).map(([product, stat]) =>
                                                                m("div", { class: "bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-100 dark:border-gray-700" }, [
                                                                    m("h4", { class: "font-semibold text-gray-700 dark:text-gray-300 mb-2 truncate" }, product),
                                                                    m("div", { class: "flex justify-between items-center text-sm mb-1" }, [
                                                                        m("span", { class: "text-gray-500 dark:text-gray-400" }, "Ditarik:"),
                                                                        m("span", { class: "font-medium text-red-600" }, this.formatCurrency(stat.withdrawn))
                                                                    ]),
                                                                    m("div", { class: "flex justify-between items-center text-sm mb-3 border-b border-gray-100 dark:border-gray-700 pb-2" }, [
                                                                        m("span", { class: "text-gray-500 dark:text-gray-400" }, "Disetor:"),
                                                                        m("span", { class: "font-medium text-green-600" }, this.formatCurrency(stat.collected))
                                                                    ]),
                                                                    // Daily Breakdown
                                                                    m("div", { class: "space-y-1" }, [
                                                                        m("p", { class: "text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" }, "Aktivitas Harian"),
                                                                        Object.entries(stat.daily)
                                                                            .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                                                                            .slice(0, 5) // Show last 5 days interacting with this product
                                                                            .map(([date, dailyStat]) =>
                                                                                m("div", { class: "flex justify-between items-center text-xs border-b border-gray-100 dark:border-gray-700 last:border-0 pb-1.5 mb-1.5 last:pb-0 last:mb-0" }, [
                                                                                    m("span", { class: "text-gray-400" }, this.formatDate(date).split(',')[0]), // Just date part
                                                                                    m("div", { class: "flex flex-col items-end space-y-1" }, [
                                                                                        dailyStat.out > 0 && m("span", { class: "text-red-500 font-medium flex items-center gap-1.5" }, [
                                                                                            `-${this.formatCurrency(dailyStat.out)}`,
                                                                                            m("span", { class: "text-[10px] text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 px-1.5 py-0.5 rounded-md font-normal" }, `${dailyStat.out_customers.size} org`)
                                                                                        ]),
                                                                                        dailyStat.in > 0 && m("span", { class: "text-green-500 font-medium flex items-center gap-1.5" }, [
                                                                                            `+${this.formatCurrency(dailyStat.in)}`,
                                                                                            m("span", { class: "text-[10px] text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 px-1.5 py-0.5 rounded-md font-normal" }, `${dailyStat.in_customers.size} org`)
                                                                                        ])
                                                                                    ])
                                                                                ])
                                                                            )
                                                                    ])
                                                                ])
                                                            )
                                                        )
                                                    ]),

                                                    // Loans Stats
                                                    Object.keys(loanStats).length > 0 && m("div", [
                                                        m("h3", { class: "text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 flex items-center" }, [
                                                            m("i", { class: "ri-hand-coin-line mr-2 text-blue-600" }),
                                                            "Pinjaman"
                                                        ]),
                                                        m("div", { class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" },
                                                            Object.entries(loanStats).map(([product, stat]) =>
                                                                m("div", { class: "bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-100 dark:border-gray-700" }, [
                                                                    m("h4", { class: "font-semibold text-gray-700 dark:text-gray-300 mb-2 truncate" }, product),
                                                                    m("div", { class: "flex justify-between items-center text-sm mb-1" }, [
                                                                        m("span", { class: "text-gray-500 dark:text-gray-400" }, "Disalurkan:"),
                                                                        m("span", { class: "font-medium text-blue-600" }, this.formatCurrency(stat.disbursed))
                                                                    ]),
                                                                    m("div", { class: "flex justify-between items-center text-sm mb-3 border-b border-gray-100 dark:border-gray-700 pb-2" }, [
                                                                        m("span", { class: "text-gray-500 dark:text-gray-400" }, "Diterima:"),
                                                                        m("span", { class: "font-medium text-green-600" }, this.formatCurrency(stat.collected))
                                                                    ]),
                                                                    // Daily Breakdown
                                                                    m("div", { class: "space-y-1" }, [
                                                                        m("p", { class: "text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" }, "Aktivitas Harian"),
                                                                        Object.entries(stat.daily)
                                                                            .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                                                                            .slice(0, 5)
                                                                            .map(([date, dailyStat]) =>
                                                                                m("div", { class: "flex justify-between items-center text-xs border-b border-gray-100 dark:border-gray-700 last:border-0 pb-1.5 mb-1.5 last:pb-0 last:mb-0" }, [
                                                                                    m("span", { class: "text-gray-400" }, this.formatDate(date).split(',')[0]),
                                                                                    m("div", { class: "flex flex-col items-end space-y-1" }, [
                                                                                        dailyStat.out > 0 && m("span", { class: "text-blue-500 font-medium flex items-center gap-1.5" }, [
                                                                                            `-${this.formatCurrency(dailyStat.out)}`,
                                                                                            m("span", { class: "text-[10px] text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 px-1.5 py-0.5 rounded-md font-normal" }, `${dailyStat.out_customers.size} org`)
                                                                                        ]),
                                                                                        dailyStat.in > 0 && m("span", { class: "text-green-500 font-medium flex items-center gap-1.5" }, [
                                                                                            `+${this.formatCurrency(dailyStat.in)}`,
                                                                                            m("span", { class: "text-[10px] text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400 px-1.5 py-0.5 rounded-md font-normal" }, `${dailyStat.in_customers.size} org`)
                                                                                        ])
                                                                                    ])
                                                                                ])
                                                                            )
                                                                    ])
                                                                ])
                                                            )
                                                        )
                                                    ]),

                                                    // Empty State
                                                    Object.keys(savingsStats).length === 0 && Object.keys(loanStats).length === 0 && m("div", { class: "text-center py-4 text-gray-500 dark:text-gray-400" }, "Belum ada aktivitas produk pada rentang tanggal ini."),

                                                    // Grouped Transaction History
                                                    filteredActivities.length > 0 && m("div", { class: "mt-8" }, [
                                                        m("div", { class: "flex justify-between items-center mb-3" }, [
                                                            m("h3", { class: "text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" }, "Riwayat Transaksi"),
                                                            m("button", {
                                                                onclick: () => this.exportToExcel(coordinator),
                                                                class: "inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                                                            }, [
                                                                m("i", { class: "ri-file-excel-2-line mr-1.5" }),
                                                                "Expor ke Excel"
                                                            ])
                                                        ]),

                                                        // Grouped List
                                                        m("div", { class: "space-y-4" },
                                                            sortedDates.map(date => {
                                                                const isDateExpanded = this.expandedDates[date] !== false; // Default to expanded? Or defaulting to null which is expanded if logic says so. Let's make default expanded.
                                                                // Actually let's make default expanded for the first date, others collapsed?
                                                                // Or just default all expanded. The user asked for accordions.
                                                                // Let's default expand the first one, or all. I'll default EXPAND all for better visibility initially.
                                                                const expanded = this.expandedDates[date] === undefined ? true : this.expandedDates[date];

                                                                return m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" }, [
                                                                    // Accordion Header
                                                                    m("button", {
                                                                        onclick: () => this.toggleDateAccordion(date),
                                                                        class: "w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-750 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                                                                    }, [
                                                                        m("div", { class: "flex items-center space-x-2" }, [
                                                                            m("i", { class: "ri-calendar-event-line text-orange-500" }),
                                                                            m("span", { class: "font-semibold text-gray-700 dark:text-gray-300" }, this.formatDate(date).split(',')[0])
                                                                        ]),
                                                                        m("div", { class: "flex items-center space-x-3" }, [
                                                                            m("span", { class: "text-xs text-gray-500 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full" }, `${groupedActivities[date].length} Transaksi`),
                                                                            m("i", { class: `ri-arrow-${expanded ? 'up' : 'down'}-s-line text-gray-500 transition-transform duration-200` })
                                                                        ])
                                                                    ]),

                                                                    // Accordion Content (Table)
                                                                    expanded && m("div", { class: "overflow-x-auto" }, [
                                                                        m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                                                                            m("thead", { class: "bg-gray-50 dark:bg-gray-800" }, [
                                                                                m("tr", [
                                                                                    m("th", { class: "px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" }, "Waktu"),
                                                                                    m("th", { class: "px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" }, "Nasabah"),
                                                                                    m("th", { class: "px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" }, "Jenis"),
                                                                                    m("th", { class: "px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" }, "Jumlah"),
                                                                                    m("th", { class: "px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" }, "Produk"),
                                                                                ])
                                                                            ]),
                                                                            m("tbody", { class: "divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800" },
                                                                                groupedActivities[date].map(activity => {
                                                                                    const isBanking = activity.collection_type === 'banking';
                                                                                    const isWithdrawal = activity.collection_type === 'withdrawal' || activity.collection_type === 'loan_disbursement';
                                                                                    const isPositive = !isBanking && !isWithdrawal;
                                                                                    const time = new Date(activity.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

                                                                                    return m("tr", [
                                                                                        m("td", { class: "px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" }, time),
                                                                                        m("td", { class: "px-4 py-2 whitespace-nowrap text-sm" }, [
                                                                                            m("div", { class: "font-medium text-gray-900 dark:text-white" }, activity.customer_name || '-'),
                                                                                            m("div", { class: "text-xs text-gray-500" }, activity.account_number || '-')
                                                                                        ]),
                                                                                        m("td", { class: "px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" },
                                                                                            activity.collection_type === 'loan_disbursement' ? 'Pencairan Pinjaman' :
                                                                                                activity.collection_type === 'banking' ? 'Setor ke Kantor' :
                                                                                                    activity.collection_type === 'withdrawal' ? 'Penarikan Simpanan' :
                                                                                                        activity.collection_type === 'deposit' ? 'Setoran Simpanan' :
                                                                                                            activity.collection_type === 'installment' ? 'Angsuran' :
                                                                                                                activity.collection_type === 'entrusted_funds' ? 'Penitipan Dana Kantor' :
                                                                                                                    activity.collection_type
                                                                                        ),
                                                                                        m("td", { class: "px-4 py-2 whitespace-nowrap text-sm font-bold" }, [
                                                                                            m("span", { class: isPositive ? 'text-green-600' : 'text-red-600' },
                                                                                                `${isPositive ? '+' : '-'}${this.formatCurrency(Math.abs(activity.collection_amount))}`
                                                                                            )
                                                                                        ]),
                                                                                        m("td", { class: "px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" }, activity.product_name || '-')
                                                                                    ]);
                                                                                })
                                                                            )
                                                                        ])
                                                                    ])
                                                                ]);
                                                            })
                                                        )
                                                    ])
                                                ])
                                            ])
                                        ])
                                    ]));
                                }

                                return acc;
                            }, [])
                        ])
                    ])
                ])
            ]),
        ]);
    }

};

export default CoordinatorAktivitas;